import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { endOfMonth, startOfMonth, subDays, subMonths } from 'date-fns';
import { Pill } from './AnalyticsSharedStyles';
import { QuickPickWrap } from './AnalyticsFilterBarStyles';
import { getDateForApi } from '../../../utils/date';

type Range = { from: string | null; to: string | null };

const computeRange = (key: string): Range => {
  const now = new Date();
  switch (key) {
    case 'last7':
      return { from: getDateForApi(subDays(now, 6)) ?? null, to: getDateForApi(now) ?? null };
    case 'last30':
      return { from: getDateForApi(subDays(now, 29)) ?? null, to: getDateForApi(now) ?? null };
    case 'thisMonth':
      return { from: getDateForApi(startOfMonth(now)) ?? null, to: getDateForApi(now) ?? null };
    case 'lastMonth': {
      const prev = subMonths(now, 1);
      return { from: getDateForApi(startOfMonth(prev)) ?? null, to: getDateForApi(endOfMonth(prev)) ?? null };
    }
    case 'notSelected':
    default:
      return { from: null, to: null };
  }
};

const OPTIONS: { key: string; labelKey: string }[] = [
  { key: 'last7', labelKey: 'analytics.filter.quickPick.last7' },
  { key: 'last30', labelKey: 'analytics.filter.quickPick.last30' },
  { key: 'thisMonth', labelKey: 'analytics.filter.quickPick.thisMonth' },
  { key: 'lastMonth', labelKey: 'analytics.filter.quickPick.lastMonth' },
  { key: 'notSelected', labelKey: 'analytics.filter.quickPick.notSelected' },
];

interface Props {
  fromName: string;
  toName: string;
}

export const AnalyticsQuickPickRow: FC<Props> = ({ fromName, toName }) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<Record<string, string | null>>();

  const activeKey = useMemo(() => {
    const from = values[fromName];
    const to = values[toName];
    if (!from && !to) return 'notSelected';

    for (const opt of OPTIONS) {
      if (opt.key === 'notSelected') continue;
      const r = computeRange(opt.key);
      if (r.from === from && r.to === to) return opt.key;
    }
    return undefined;
  }, [values, fromName, toName]);

  const handlePick = (key: string) => {
    const r = computeRange(key);
    setFieldValue(fromName, r.from);
    setFieldValue(toName, r.to);
  };

  return (
    <QuickPickWrap>
      {OPTIONS.map((opt) => (
        <Pill key={opt.key} type="button" selected={activeKey === opt.key} onClick={() => handlePick(opt.key)}>
          {t(opt.labelKey)}
        </Pill>
      ))}
    </QuickPickWrap>
  );
};
